import { useAuth } from "./AuthProvider";
import { usePrevious } from "@uidotdev/usehooks";
import React, { useContext, useEffect } from "react";
import { setCookie } from "nookies";
import { mixpanel, preloadMixpanelWhenIdle } from "../utils/analytics";
import { useLocale } from "@jammable/ui-core/src/i18n";

const AnalyticsContext = React.createContext<typeof mixpanel | null>(null);

export function AnalyticsProvider(props: { children: React.ReactNode }) {
  const { children } = props;

  const auth: { user?: { uid: string; email: string } } = useAuth();

  const userId = auth.user?.uid;

  const prevUserId = usePrevious(userId);

  // Language the site is shown in, on every event, to measure the Spanish
  // and Portuguese localisation (Oct 2026). url = the page's language on /es
  // and /pt, else the visitor's choice or browser language.
  const { locale, fromUrl } = useLocale();
  useEffect(() => {
    mixpanel.onReady((m) => {
      m.register({ ui_locale: locale, ui_locale_from_url: fromUrl });
    });
  }, [locale, fromUrl]);

  useEffect(() => {
    // Load mixpanel once the browser is idle (off the hydration critical path),
    // then set the deviceId cookie. The cookie is read server-side to associate
    // anonymous events with a user once they authenticate. Deferring it a few
    // seconds is fine for that purpose.
    mixpanel.onReady((m) => {
      setCookie(null, "deviceId", m.get_property("$device_id"));
    });
    preloadMixpanelWhenIdle();
  }, []);

  useEffect(() => {
    if (!prevUserId && userId) {
      mixpanel.identify(userId);
      mixpanel.people.set({ $email: auth.user?.email });
    } else if (prevUserId && !userId) {
      mixpanel.reset();
    }
  }, [prevUserId, userId, auth.user]);

  return (
    <AnalyticsContext.Provider value={mixpanel}>
      {children}
    </AnalyticsContext.Provider>
  );
}

export function useAnalytics() {
  const ctx = useContext(AnalyticsContext);

  if (!ctx) {
    throw new Error("Must wrap consumer with an <AnalyticsProvider />");
  }

  return ctx;
}
